import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api-typed";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import type { BodyOf, Schemas } from "@/types/api";

// ============================================================================
// Tipos — os do backend, gerados do contrato (`src/types/api.gen.ts`)
// ============================================================================

/** Os sete sinais do capítulo §4.7. Três já calculados noutros serviços,
 * quatro novos — ver `app/services/compliance_issues.py`. */
export type ComplianceIssueKind = Schemas["ComplianceIssueRead"]["kind"];

export type ComplianceIssueSeverity = Schemas["ComplianceIssueRead"]["severity"];

/**
 * `aceite` é sempre uma decisão de pessoa — o detector nunca a atribui
 * sozinho, e nunca a desfaz enquanto a condição continuar a verificar-se.
 */
export type ComplianceIssueStatus = Schemas["ComplianceIssueRead"]["status"];

export type ComplianceIssue = Schemas["ComplianceIssueRead"];


export type ComplianceIssueUpdateInput = BodyOf<"/compliance-issues/{issue_id}", "patch">;

// Fechar/reabrir/atribuir mexe na listagem, no resumo e no histórico da
// própria ocorrência — invalidar os três a cada mutação.
const KEYS = [["compliance-issues"], ["compliance-issues-summary"]];

// ============================================================================
// Leitura
// ============================================================================

export interface ComplianceIssuesPageParams {
  /** `abertas` = aberta + em análise (o que ainda precisa de alguém); os outros são um estado só. Vazio = todos. */
  estado?: "abertas" | "em_analise" | "resolvida" | "aceite";
  severidade?: string;
  kind?: string;
  q?: string;
  page: number;
  pageSize: number;
}

/**
 * Uma página de ocorrências e quantas há ao todo: filtra, ordena (da mais grave para a menos grave) e pagina no servidor.
 * A lista simples corta em 500 e a EPEL chegou a ter mais de 2 000 abertas.
 */
export const useComplianceIssuesPage = ({ estado, severidade, kind, q, page, pageSize }: ComplianceIssuesPageParams) =>
  useQuery({
    queryKey: ["compliance-issues", "page", { estado, severidade, kind, q, page, pageSize }],
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get("/compliance-issues/page", {
        query: {
          abertas: estado === "abertas" ? true : undefined,
          estado: estado && estado !== "abertas" ? estado : undefined,
          severidade: severidade || undefined,
          kind: kind || undefined,
          q: q || undefined,
          page,
          page_size: pageSize,
        },
      }),
  });

export const useComplianceIssueSummary = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ["compliance-issues-summary"],
    enabled,
    queryFn: () => api.get("/compliance-issues/summary"),
  });


// ============================================================================
// Escrita
// ============================================================================

export const useUpdateComplianceIssue = () => {
  const queryClient = useQueryClient();
  return useMutationWithToast({
    mutationFn: ({ id, data }: { id: string; data: ComplianceIssueUpdateInput }) =>
      api.patch("/compliance-issues/{issue_id}", { path: { issue_id: id }, body: data }),
    invalidateQueries: (_data, { id }) => [...KEYS, ["compliance-issue-history", id]],
    onSuccessCallback: () => {
      // O histórico é lido por id — invalidar a query genérica acima não
      // chega quando o sheet já está aberto na mesma ocorrência.
      void queryClient.invalidateQueries({ queryKey: ["compliance-issue-history"] });
    },
  });
};

/** Corre os detectores agora, em vez de esperar pelo cron das 03:15. */
export const useScanComplianceIssues = () =>
  useMutationWithToast({
    mutationFn: () => api.post("/compliance-issues/scan"),
    successMessage: (data) =>
      data.total_activo === 0
        ? "Sem ocorrências ativas."
        : `Análise concluída: ${data.criadas} nova(s), ${data.reabertas} reaberta(s), ${data.fechadas} fechada(s).`,
    errorMessage: "Não foi possível correr a análise agora.",
    invalidateQueries: KEYS,
  });
